import React, { useEffect, useMemo, useRef, useState } from 'react';

// @ts-ignore-next-line no types
import matrix from 'calendar-matrix';
import axios from 'axios';
import { Popover } from 'react-tiny-popover';
import { numpad } from '../../../../../../shared/lib/time';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { debounce } from 'debounce-promise-with-cancel';
import { EmbedConfig } from '../..';

import './styles/style.scss';
import { ExportV2LimitedResult } from '../../../../../../shared/types/exportV2';
import { DailyProgramEvent } from '../../../../../../shared/types/dailyProgram';
import { mapBy } from '../../../../../../shared/lib/functional';
import { PropertyZone } from '../../../../../../shared/types/property';

interface AppPropsBase {
  config: EmbedConfig;
}

type AppProps = AppPropsBase & React.HTMLProps<HTMLDivElement>;

function getDefaultGridSize(size: number, date?: Date) {
  const gridSize = Array(size).fill(1);
  if (date) gridSize[date.getDay()] = 1;
  return gridSize;
}

let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
type EmbedEvent = DailyProgramEvent & { zones: PropertyZone[] };

let popoverOutDelay: NodeJS.Timeout | undefined;
let monthDelay: NodeJS.Timeout | undefined;

function App(props: AppProps) {
  const { config, className, ...rest } = props;
  const currentDate = new Date();

  const [monthDate, setMonthDate] = useState(currentDate);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  const dayMatrix: number[][] = matrix(monthDate);

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<EmbedEvent[][]>([[]]);
  const [popoverStates, setPopoverStates] = useState<{ [key: string]: boolean }>({});
  const [gridColSizes, setGridColSizes] = useState(getDefaultGridSize(7, currentDate));
  const [gridRowSizes, setGridRowSizes] = useState(
    getDefaultGridSize(dayMatrix.length, currentDate)
  );

  const [lastScroll, setLastScroll] = useState<string | undefined>();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [gridWidth, setGridWidth] = useState(0);
  const [_, setGridHeight] = useState(0);
  const [popoverActive, setPopoverActive] = useState(false);
  const [hoverCell, setHoverCell] = useState(-99);
  const [focusCell, setFocusCell] = useState(currentDate.getDate());
  const [scrollCellReset, setScrollCellReset] = useState(-99);
  const [zones, setZones] = useState<string[]>([]);
  const [selectedZone, setSelectedZone] = useState<string | undefined>();
  const [firstLoad, setFirstLoad] = useState(false);
  const [firstScroll, setFirstScroll] = useState(false);
  const [tick, setTick] = useState(0);

  const gridDiv = useRef<HTMLDivElement>(null);
  const stickyDiv = useRef<HTMLDivElement>(null);

  const isMobile = windowWidth < 620;

  useMemo(() => {
    if (isMobile) {
      togglePopover(undefined, false);
    }
  }, [isMobile]);

  useEffect(() => {
    fetchEvents(monthDate).catch(console.error);

    return () => {
      if (popoverOutDelay) clearTimeout(popoverOutDelay);
      if (monthDelay) clearTimeout(monthDelay);

      resetState();
    };
  }, []);

  useEffect(() => {
    const states = makePopoverStates();
    setPopoverStates(states);
  }, [events]);

  useEffect(() => {
    const popoverActive = Object.values(popoverStates).includes(true);

    if (popoverOutDelay) clearTimeout(popoverOutDelay);

    if (popoverActive) {
      setPopoverActive(true);
    } else {
      const timeout = setTimeout(() => {
        setPopoverActive(false);
      }, config.animationDuration);

      popoverOutDelay = timeout;
    }
  }, [popoverStates]);

  useEffect(() => {
    if (!gridDiv.current) return;

    const resizeObserver = new ResizeObserver(() => {
      setWindowWidth(window.innerWidth);
      setGridWidth(gridDiv.current?.offsetWidth ?? 0);
      setGridHeight(gridDiv.current?.offsetHeight ?? 0);
    });

    resizeObserver.observe(gridDiv.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [gridDiv.current]);

  useEffect(() => {
    resetState();
  }, [monthDate]);

  useEffect(() => {
    updateSelectedDate();
  }, [focusCell]);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    const interval = setInterval(() => {
      setTick(t => Math.min(t + 1, 50));
    }, 100);

    return () => {
      window.removeEventListener('scroll', onScroll);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    scrollToExperiences();
  }, [tick]);

  function onScroll() {
    const st = window.pageYOffset || document.documentElement.scrollTop;

    if (st > lastScrollTop) {
      setLastScroll('down');
    } else if (st < lastScrollTop) {
      setLastScroll('up');
    }

    lastScrollTop = st <= 0 ? 0 : st;
  }

  function updateGrid(d: number | undefined) {
    let x = -1;
    let y = -1;

    const newGridColSize = getDefaultGridSize(7);
    const newGridRowSize = getDefaultGridSize(dayMatrix.length);

    if (d !== undefined && d > 0) {
      dayMatrix.forEach((wk, i) => {
        if (x >= 0) return;

        x = wk.findIndex(e => d === e);

        if (x >= 0) {
          y = i;
        }
      });

      newGridColSize[x] = config.expandedRowColSize;
      newGridRowSize[y] = config.expandedRowColSize;
    }

    setGridColSizes(newGridColSize);
    setGridRowSizes(newGridRowSize);
  }

  function updateSelectedDate() {
    if (focusCell < 0) return;

    const monthIso = monthDate.toISOString();
    const monthTokens = monthIso.split('-');
    monthTokens[monthTokens.length - 1] = numpad(focusCell);

    const date = new Date(monthTokens.join('-'));
    const timezoneOffset = date.getTimezoneOffset() * 60000;
    const newDate = new Date(date.valueOf() + timezoneOffset);

    setSelectedDate(newDate);
  }

  function resetState() {
    setPopoverStates({});
    setGridColSizes(getDefaultGridSize(7, currentDate));
    setGridRowSizes(getDefaultGridSize(dayMatrix.length, currentDate));

    setHoverCell(-99);
    setFocusCell(
      currentDate.getMonth() === monthDate.getMonth() &&
        currentDate.getFullYear() == monthDate.getFullYear()
        ? currentDate.getDate()
        : 1
    );
    setPopoverActive(false);
    updateSelectedDate();

    if (popoverOutDelay) clearTimeout(popoverOutDelay);
  }

  function makePopoverStates() {
    const states: { [key: string]: boolean } = {};

    events.flat().forEach(e => {
      states[e.id] = false;
    });

    return states;
  }

  async function fetchEvents(date: Date) {
    setLoading(true);

    try {
      const dateIso = new Date(date).toISOString().slice(0, 8) + '01';
      const days = getNumOfDays();

      const response = await axios.get<ExportV2LimitedResult>(config.dataUrl, {
        params: { date: dateIso, days },
      });

      const zoneDict = mapBy(response.data.collections.zones, '_id');

      const data = response.data.lineup.map(l =>
        l.events.map(e => ({
          ...e,
          zones: e.zoneIds.map(a => zoneDict[a]),
        }))
      );

      setEvents(data);
      setZones(response.data.collections.zones.map(a => a.name));
    } catch (error) {
      setEvents([[]]);
      throw error;
    } finally {
      setLoading(false);
      setFirstLoad(true);
    }
  }

  function scrollToExperiences() {
    if (firstLoad && firstScroll) return;
    const targetElement = document.getElementById('experience-view');

    if (targetElement) {
      const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY;
      const currentScroll = window.scrollY;
      const viewportHeight = window.innerHeight;

      // Only scroll if the target element is below the current viewport
      if (targetPosition > currentScroll + viewportHeight) {
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth',
        });
        setFirstScroll(true);
      }
    }
  }

  async function changeMonth(
    direction: 1 | -1,
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    if (e) {
      e.stopPropagation();
    }

    const copyDate = new Date(monthDate);
    copyDate.setMonth(copyDate.getMonth() + direction, 1);

    setEvents([]);
    setLoading(true);
    setMonthDate(copyDate);

    if (monthDelay) clearTimeout(monthDelay);

    const timeout = setTimeout(() => {
      fetchEvents(copyDate).catch(console.error);
    }, config.animationDuration * 2);

    monthDelay = timeout;

    handleDateClick();
  }

  async function resetMonth(e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    if (e) {
      e.stopPropagation();
    }

    const copyDate = new Date(currentDate);

    setEvents([]);
    setLoading(true);
    setMonthDate(copyDate);

    if (monthDelay) clearTimeout(monthDelay);

    fetchEvents(copyDate).catch(console.error);

    handleDateClick();
  }

  function handleDateClick() {
    if (!stickyDiv.current) return;

    if (isMobile) {
      const divToScroll =
        (config.scrollDiv ? document.getElementById(config.scrollDiv) : undefined) || window;

      divToScroll.scrollBy(0, stickyDiv.current.getBoundingClientRect().top + config.scrollOffset);
    }
  }

  async function handleCellMouseOver(i: number, d: number) {
    if (hoverCell === d || popoverActive || loading) return;

    setHoverCell(d < 0 ? -99 : d);

    if (gridDiv.current && scrollCellReset != i) {
      gridDiv.current.querySelectorAll(`.go-cell-${scrollCellReset}`).forEach(div => {
        div.children[0].scrollTo(0, 0);
      });
    }

    setScrollCellReset(i);
    updateGrid(d);
  }

  const handleCellMouseOverDebounced = debounce(
    handleCellMouseOver,
    config.animationDuration * 1.5
  );

  function handleGridMouseOut(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    handleCellMouseOverDebounced.cancel();
    const from = e.relatedTarget as HTMLDivElement;

    if (!gridDiv || !gridDiv.current || popoverActive) return;

    if (!gridDiv.current.contains(from)) {
      setHoverCell(-99);
      updateGrid(undefined);

      gridDiv.current.querySelectorAll(`.go-cell-container`).forEach(div => {
        div.scrollTo(0, 0);
      });
    }
  }

  function handleCellClick(d: number, e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    if (!(e.target as HTMLElement).className.includes('go-event')) {
      togglePopover(undefined, false);
    }
    setHoverCell(d);
    setFocusCell(d);
    updateGrid(d);
  }

  function getNumOfDays() {
    return Math.max(...dayMatrix.flat());
  }

  function togglePopover(
    eId: string | undefined,
    state: boolean,
    clickEvt?: React.MouseEvent<HTMLDivElement, MouseEvent>,
    force?: boolean
  ) {
    if (clickEvt) {
      clickEvt.stopPropagation();
    }

    const states = makePopoverStates();

    if (eId && (force || !popoverActive)) {
      states[eId] = state;
    }

    setPopoverStates(states);
  }

  function filterEvents(events: EmbedEvent[], filter: string | undefined) {
    return events.filter(e => !filter || e.zones.map(z => z.name).includes(filter));
  }

  function isExpanded(i: number) {
    return gridColSizes[i % 7] != 1;
  }

  function isActive(i: number, d: number) {
    return isExpanded(i) && hoverCell === d;
  }

  function isCurrentDay(d: number) {
    return (
      currentDate.getMonth() === monthDate.getMonth() &&
      currentDate.getFullYear() === monthDate.getFullYear() &&
      d === currentDate.getDate()
    );
  }

  function isFirstRow(d: number) {
    return dayMatrix[0].findIndex(d2 => d2 === d) > -1;
  }

  function isFirstCol(d: number) {
    return (
      dayMatrix
        .map(wk => wk[0])
        .flat()
        .findIndex(d2 => d2 === d) > -1
    );
  }

  const gridDirty = isMobile ? false : gridColSizes.findIndex(e => e != 1) > -1;
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const classes = [className];

  if (isMobile) {
    classes.push('go-is-mobile');
  }

  if (lastScroll) {
    classes.push(`go-last-scroll-${lastScroll}`);
  }

  return (
    <div
      id="go-app"
      ref={stickyDiv}
      style={{ minHeight: isMobile ? '100vh' : undefined }}
      className={classes.join(' ')}
      {...rest}
    >
      <div
        className={`${
          isMobile ? 'sticky transition-all bg-background border-b-1 border-table' : ''
        } px-3 pt-3 pb-2 overflow-x-auto flex items-center go-heading`}
        style={{ zIndex: 2 }}
      >
        {selectedDate && (
          <div
            className={`transition-all whitespace-nowrap text-accent font-bold mr-2${
              isMobile ? ' underline' : ''
            } go-current-date`}
            style={{ opacity: loading ? 0.5 : 1 }}
            onClick={handleDateClick}
          >
            {selectedDate.toLocaleDateString('default', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </div>
        )}
        <select
          value={selectedZone}
          onChange={e => setSelectedZone(e.target.value === 'none' ? undefined : e.target.value)}
          className={`text-accent${
            isMobile ? ' grow' : ' w-64'
          } truncate px-2 py-1 mr-2 border rounded-full border-accent whitespace-nowrap min-w-0 go-attribute-select`}
        >
          <option value="none">Filter: All</option>
          {zones.map(z => (
            <option key={z} value={z} className="go-event-zone">
              {z}
            </option>
          ))}
        </select>
        <div className="ml-auto whitespace-nowrap overflow-clip border rounded-full border-accent flex items-stretch go-actions">
          <button className="pr-1 pl-2 go-button go-prev-month" onClick={e => changeMonth(-1, e)}>
            <span className="text-accent goapp-icon-arrow-left"></span>
          </button>
          <button
            className="text-accent py-1 text-center w-12 go-button go-curr-month"
            onClick={resetMonth}
          >
            {monthDate.toLocaleDateString('default', {
              month: 'short',
            })}
          </button>
          <button className="pl-1 pr-2 go-button go-next-month" onClick={e => changeMonth(1, e)}>
            <span className="text-accent goapp-icon-arrow-right"></span>
          </button>
        </div>
      </div>
      <div
        className={`py-2 grid transition-all${loading ? ' opacity-50' : ''} go-day-headings`}
        style={{
          gridTemplateColumns: (isMobile ? getDefaultGridSize(7) : gridColSizes)
            .map(x => `minmax(0,${x}fr)`)
            .join(' '),
        }}
      >
        {days.map((d, i) => (
          <div
            key={d}
            className={`text-center text-secondary${
              !isMobile && isExpanded(i) ? ' text-accent' : ''
            } go-day-name go-day-name-${i}`}
          >
            {d}
          </div>
        ))}
      </div>
      <div
        ref={gridDiv}
        className={`grid transition-all${loading ? ' opacity-50' : ''} go-grid`}
        style={{
          height: (dayMatrix.length * gridWidth) / 7,
          gridTemplateColumns: (isMobile ? getDefaultGridSize(7) : gridColSizes)
            .map(x => `minmax(0,${x}fr)`)
            .join(' '),
          gridTemplateRows: (isMobile ? getDefaultGridSize(dayMatrix.length) : gridRowSizes)
            .map(x => `minmax(0,${x}fr)`)
            .join(' '),
        }}
        onMouseOut={handleGridMouseOut}
      >
        {dayMatrix.flat().map((d, i) => (
          <div
            key={d}
            className={`relative w-full transition-all${
              isFirstRow(d)
                ? ''
                : isCurrentDay(d) || d === focusCell || d === hoverCell
                ? ' border-t-2'
                : ' border-t'
            }${
              isFirstCol(d)
                ? ''
                : isCurrentDay(d) || d === focusCell || d === hoverCell
                ? ' border-l-2'
                : ' border-l'
            }${
              d === focusCell
                ? ' border-active'
                : d === hoverCell
                ? ' border-accent'
                : isCurrentDay(d)
                ? ' border-emphasis'
                : ' border-table'
            }${gridDirty ? (isActive(i, d) ? '' : ' opacity-50') : ''} go-cell go-cell-${i}${
              isCurrentDay(d) ? ' active' : ''
            }`}
          >
            <div
              className="full go-cell-container"
              style={{
                opacity: isActive(i, d) ? 1 : !gridDirty ? 1 : 0.5,
                overflowY: isActive(i, d) ? 'auto' : !gridDirty ? 'auto' : 'hidden',
              }}
              onMouseOver={() => handleCellMouseOverDebounced(i, d)}
              onClick={e => handleCellClick(d, e)}
            >
              {isMobile && filterEvents(events[d - 1] || [], selectedZone).length > 0 && (
                <div
                  className="bg-emphasis go-cell-marker"
                  style={{
                    position: 'absolute',
                    bottom: 5,
                    right: 5,
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                  }}
                ></div>
              )}
              <div
                className={`pl-2 pt-1 pr-0 font-bold${
                  focusCell === d
                    ? ' text-active'
                    : hoverCell === d
                    ? ' text-accent'
                    : isCurrentDay(d)
                    ? ' text-emphasis'
                    : d < 0
                    ? ' text-disabled'
                    : ' text-secondary'
                } go-day`}
              >
                {Math.abs(d)}
              </div>
              <div className={`${d < 1 ? 'hidden ' : ''}go-events`}>
                {!isMobile &&
                  filterEvents(events[d - 1] || [], selectedZone)
                    .slice(0, isActive(i, d) ? undefined : 5)
                    .map(evt => (
                      // @ts-ignore
                      <Popover
                        key={evt.id}
                        boundaryElement={gridDiv.current || undefined}
                        parentElement={gridDiv.current || undefined}
                        isOpen={popoverStates[evt.id]}
                        positions={['right', 'left', 'top', 'bottom']} // preferred positions by priority
                        containerStyle={{ zIndex: '3' }}
                        content={
                          <PopoverContent
                            event={evt}
                            config={config}
                            togglePopover={togglePopover}
                            imgClassName="-mx-3"
                            className={`w-80 bg-white p-3 border border-table shadow-lg go-event-popover go-event-popover-${i}`}
                          />
                        }
                      >
                        <div
                          className={`cursor-pointer py-1 pl-2 pr-0 flex${
                            popoverStates[evt.id] ? ' bg-highlight' : ''
                          } content-stretch go-event go-event-${i}`}
                          onClick={() =>
                            togglePopover(evt.id, !popoverStates[evt.id], undefined, isActive(i, d))
                          }
                        >
                          {/* {evt.images[0] && (
                            <div
                              className={`transition-all${
                                isActive(i, d) ? ' mr-2 go-event-img-width' : ' go-event-img-hide'
                              } flex-none go-event-img-wrapper`}
                            >
                              <div className="rectangle relative border-1 overflow-clip go-event-img-container">
                                <LazyLoadImage
                                  className="full go-event-img"
                                  wrapperClassName="full"
                                  loading="lazy"
                                  src={evt.images[0].url}
                                  effect="blur"
                                />
                              </div>
                            </div>
                          )} */}
                          <div className="w-full flex-grow overflow-x-hidden go-event-container">
                            <div className="flex space-x-1">
                              {evt.zones.map(z => (
                                <div
                                  key={z.name}
                                  className="text-white shrink-0 min-w-0 bg-black font-bold rounded-full px-1 text-xs truncate go-event-zone"
                                  style={{ backgroundColor: z.color }}
                                >
                                  {z.name}
                                </div>
                              ))}
                            </div>
                            <div className="text-accent text-sm font-bold truncate go-event-name">
                              {evt.title}
                            </div>
                            <div className="text-sm truncate go-event-time">
                              {/* FIXME: Why is this an array sometimes? */}
                              {evt.timeString.toString()}
                            </div>
                          </div>
                        </div>
                      </Popover>
                    ))}
              </div>
            </div>
          </div>
        ))}
      </div>
      {isMobile && (
        <div className="grid gap-3 go-mobile-events">
          {filterEvents(events[focusCell - 1] || [], selectedZone).map((evt, i) => (
            <PopoverContent
              key={evt.id}
              event={evt}
              config={config}
              togglePopover={togglePopover}
              noClose
              imgClassName="-mx-3"
              className={`w-full p-3 border-t border-b-1 border-table bg-background go-event-popover go-event-popover-${i}`}
            />
          ))}
          {filterEvents(events[focusCell - 1] || [], selectedZone).length < 1 && (
            <div className="p-3 border-t border-table italic text-disabled text-center">
              {loading ? 'Loading...' : 'No content available'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface PopoverContentPropsBase {
  event: EmbedEvent;
  config: EmbedConfig;
  imgClassName?: string;
  noClose?: boolean;
  togglePopover: (
    eId: string | undefined,
    state: boolean,
    clickEvt: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => void;
}

type PopoverContentProps = PopoverContentPropsBase & React.HTMLProps<HTMLDivElement>;

export function PopoverContent(props: PopoverContentProps) {
  const { event, config, className, togglePopover, noClose, imgClassName, ...rest } = props;

  const classes = ['relative', className];

  return (
    <div className={classes.join(' ')} {...rest}>
      {!noClose && (
        <div
          className="bg-accent cursor-pointer go-event-close"
          onClick={e => togglePopover(event.id, false, e)}
        >
          <span className="text-white goapp-icon-close"></span>
        </div>
      )}
      {event.images[0] && (
        <div className={[imgClassName || '', `-mt-3 mb-2 go-ex-evt-img-wrapper`].join(' ')}>
          <div className="rectangle relative border-1/2 overflow-clip go-ex-evt-img-container">
            <LazyLoadImage
              className="full go-event-img"
              wrapperClassName="full"
              loading="lazy"
              src={event.images[0].url}
              effect="blur"
            />
          </div>
        </div>
      )}
      <div className="w-full flex-grow go-ex-event-container">
        <div className="flex flex-wrap space-x-1">
          {event.zones.map(z => (
            <div
              key={z.name}
              className="text-white shrink-0 min-w-0 bg-black font-bold rounded-full px-1 text-xs truncate go-event-zone"
              style={{ backgroundColor: z.color }}
            >
              {z.name}
            </div>
          ))}
        </div>
        <div className="text-accent text-sm font-bold go-ex-event-name">{event.title}</div>
        <div className="text-sm italic go-ex-event-venue">{event.venueString}</div>
        <div className="text-sm go-ex-event-time">
          {/* FIXME: Why is this an array sometimes? */}
          {event.timeString.toString()}
        </div>
        {event.description && (
          <div className="text-sm mt-2 go-ex-event-description">{event.description}</div>
        )}
        {event.callToActions.length > 0 && (
          <div className="mt-2">
            {event.callToActions.map(cta => (
              <div className="relative">
                <a
                  className="text-sm font-bold text-emphasis stretched-link go-ex-event-cta"
                  href={cta.link}
                  target={cta.target}
                >
                  {cta.label}
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
